/**
 * Google Analytics 4 utility functions
 * Handles event tracking and user identification
 */

// GA4 Measurement ID - should be set via environment variable
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

/**
 * Check if gtag is available
 */
const isGtagAvailable = () => {
	return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Get UTM parameters from URL and storage
 */
const getUTMParameters = () => {
	const params = {};
	
	// Check URL parameters first
	const urlParams = new URLSearchParams(window.location.search);
	const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
	
	utmParams.forEach(param => {
		const value = urlParams.get(param);
		if (value) {
			params[param] = value;
		}
	});
	
	// If no UTM in URL, try to get from sessionStorage (saved on first visit)
	if (Object.keys(params).length === 0) {
		const savedUTM = sessionStorage.getItem('utm_params');
		if (savedUTM) {
			try {
				const parsed = JSON.parse(savedUTM);
				Object.assign(params, parsed);
			} catch (e) {
				console.warn('Failed to parse saved UTM params:', e);
			}
		}
	}
	
	return params;
};

/**
 * Save UTM parameters to sessionStorage
 */
export const saveUTMParameters = () => {
	const urlParams = new URLSearchParams(window.location.search);
	const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
	const params = {};
	
	utmParams.forEach(param => {
		const value = urlParams.get(param);
		if (value) {
			params[param] = value;
		}
	});
	
	if (Object.keys(params).length > 0) {
		sessionStorage.setItem('utm_params', JSON.stringify(params));
	}
};

/**
 * Initialize GA4
 */
export const initGA4 = () => {
	if (!isGtagAvailable()) {
		console.warn('gtag is not available. Make sure GTM or gtag.js is loaded.');
		return;
	}
	
	// Save UTM parameters on initialization
	saveUTMParameters();
	
	// Configure GA4
	window.gtag('config', GA4_MEASUREMENT_ID, {
		page_path: window.location.pathname,
		page_title: document.title,
	});
};

/**
 * Track custom event
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
	if (!isGtagAvailable()) {
		console.warn('gtag is not available. Event not tracked:', eventName);
		return;
	}
	
	// Merge UTM parameters with event parameters
	const utmParams = getUTMParameters();
	const mergedParams = {
		...utmParams,
		...eventParams,
	};
	
	window.gtag('event', eventName, mergedParams);
	
	// Debug log in development
	if (process.env.NODE_ENV === 'development') {
		console.log('🔵 GA4 Event:', eventName, mergedParams);
	}
};

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
	if (!isGtagAvailable()) {
		return;
	}
	
	// Send page_view event with parameters
	trackEvent('page_view', {
		page_path: pagePath,
		page_title: pageTitle,
		page_location: window.location.href,
	});
};

// Event tracking functions for specific events from TZ

/**
 * Track demo started
 */
export const trackDemoStarted = () => {
	trackEvent('demo_started');
};

/**
 * Track demo completed
 * @param {number} lessonId - Lesson ID
 */
export const trackDemoCompleted = (lessonId) => {
	trackEvent('demo_completed', {
		lesson_id: lessonId,
	});
};

/**
 * Track onboarding start
 */
export const trackOnboardingStart = () => {
	trackEvent('onboarding_start');
};

/**
 * Track survey completed
 * @param {string} levelCefr - CEFR level (A1-C2)
 * @param {string} goal - Goal (work/travel/study/social/kids)
 * @param {string} schedule - Schedule (2x15, 3x15, 5x15)
 */
export const trackSurveyCompleted = (levelCefr, goal, schedule) => {
	trackEvent('survey_completed', {
		level_cefr: levelCefr,
		goal: goal,
		schedule: schedule,
	});
};

/**
 * Track starter plan shown
 */
export const trackStarterPlanShown = () => {
	trackEvent('starter_plan_shown');
};

/**
 * Check if this is the first lesson for the user
 * @param {number} lessonId - Lesson ID
 * @returns {boolean} - True if this is the first lesson
 */
const isFirstLesson = (lessonId) => {
	const lesson1Opened = localStorage.getItem('lesson1_opened');
	return !lesson1Opened;
};

/**
 * Mark lesson as first lesson opened
 * @param {number} lessonId - Lesson ID
 */
const markFirstLessonOpened = (lessonId) => {
	if (!localStorage.getItem('lesson1_opened')) {
		localStorage.setItem('lesson1_opened', 'true');
		localStorage.setItem('lesson1_id', lessonId.toString());
	}
};

/**
 * Track lesson opened (first lesson interaction)
 * @param {number} lessonId - Lesson ID
 */
export const trackLessonOpened = (lessonId) => {
	if (isFirstLesson(lessonId)) {
		markFirstLessonOpened(lessonId);
		trackEvent('lesson1_opened', {
			lesson_id: lessonId,
		});
	}
};

/**
 * Track lesson completed
 * @param {number} lessonId - Lesson ID
 */
export const trackLessonCompleted = (lessonId) => {
	const lesson1Id = localStorage.getItem('lesson1_id');
	if (lesson1Id && parseInt(lesson1Id) === lessonId) {
		trackEvent('lesson1_completed', {
			lesson_id: lessonId,
		});
	}
};

/**
 * Track signup shown
 */
export const trackSignupShown = () => {
	trackEvent('signup_shown');
};

/**
 * Track signup completed
 * @param {string} method - Signup method (email, Google, etc.)
 */
export const trackSignupCompleted = (method = 'email') => {
	trackEvent('signup_completed', {
		method: method,
	});
};

/**
 * Track video watched
 * @param {number} lessonId - Lesson ID
 * @param {number} seconds - Seconds watched
 */
export const trackVideoWatched = (lessonId, seconds) => {
	trackEvent('video_watched', {
		lesson_id: lessonId,
		seconds: seconds,
	});
};

/**
 * Track quiz completed
 * @param {number} lessonId - Lesson ID
 * @param {number} score - Quiz score
 */
export const trackQuizCompleted = (lessonId, score) => {
	trackEvent('quiz_completed', {
		lesson_id: lessonId,
		score: score,
	});
};

/**
 * Track AI dialogue completed
 * @param {number} lessonId - Lesson ID
 * @param {number} turns - Number of turns
 */
export const trackAIDialogueCompleted = (lessonId, turns) => {
	trackEvent('ai_dialogue_completed', {
		lesson_id: lessonId,
		turns: turns,
	});
};

/**
 * Track trial started
 */
export const trackTrialStarted = () => {
	trackEvent('trial_started');
};
